import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Plus, RefreshCw, X as XIcon, Building2, History } from "lucide-react";
import {
  platformGetBrand, platformListProperties, platformCreateProperty,
  platformPatchProperty, platformChangePropertyStatus, platformListPropertyAudit,
} from "../../lib/api";

const PAGE_SIZE = 25;
const TYPES = [
  { v: "serviced_apartment", l: "Serviced Apartment" },
  { v: "short_let",          l: "Short Let" },
  { v: "boutique_hotel",     l: "Boutique Hotel" },
  { v: "hotel",              l: "Hotel" },
];
const STATUS_HELP = "This property cannot be activated for Concierge use until future readiness checks are complete.";

/**
 * Sprint 2 — Platform Properties (Draft/Inactive only).
 *
 * Reuses the existing Editorial Residence admin theme. Does not touch the
 * existing WLS Properties panel (which reads from `db.properties`).
 * Every mutation flows through the versioned Platform API and is audited.
 */
export default function PlatformPropertiesPanel() {
  const [brand, setBrand] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [statusF, setStatusF] = useState("");
  const [typeF, setTypeF] = useState("");
  const [searchF, setSearchF] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);  // null | {mode: "create"|"edit", data?}
  const [historyId, setHistoryId] = useState(null);

  const load = async (o = 0) => {
    setLoading(true); setError(null);
    try {
      const params = { limit: PAGE_SIZE, offset: o };
      if (statusF) params.status = statusF;
      if (typeF) params.propertyType = typeF;
      if (searchF.trim()) params.search = searchF.trim();
      const [d, b] = await Promise.all([
        platformListProperties(params),
        brand ? Promise.resolve(brand) : platformGetBrand(),
      ]);
      setItems(d.items); setTotal(d.total); setOffset(o);
      if (!brand) setBrand(b);
    } catch (e) { setError(readError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(0); }, []); // eslint-disable-line

  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  return (
    <div data-testid="admin-platform-properties">
      <header className="mb-7 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="ed-overline mb-2 flex items-center gap-2">
            <Building2 size={12} strokeWidth={1.8} className="text-[var(--ed-emerald)]" />
            System
          </p>
          <h1 className="font-serif text-3xl sm:text-4xl text-[var(--ed-ink)]">Platform Properties</h1>
          <p className="text-[var(--ed-ink-muted)] text-sm mt-1.5 max-w-2xl">
            Manage tenant-owned platform property records. Drafts only — {STATUS_HELP}
          </p>
        </div>
        <button
          data-testid="platform-property-new"
          onClick={() => setEditing({ mode: "create" })}
          className="text-[0.7rem] uppercase tracking-[0.22em] py-2.5 px-4 bg-[var(--ed-ink)] text-white hover:bg-[var(--ed-emerald)] transition-colors rounded-sm flex items-center gap-2"
        >
          <Plus size={14} /> New Property
        </button>
      </header>

      {/* Filters */}
      <form onSubmit={(e) => { e.preventDefault(); load(0); }}
            className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-5">
        <select data-testid="pp-filter-status" value={statusF} onChange={(e) => setStatusF(e.target.value)}
                className={fieldCls} aria-label="Filter by status">
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="inactive">Inactive</option>
        </select>
        <select data-testid="pp-filter-type" value={typeF} onChange={(e) => setTypeF(e.target.value)}
                className={fieldCls} aria-label="Filter by property type">
          <option value="">All types</option>
          {TYPES.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
        </select>
        <input data-testid="pp-filter-search" value={searchF} onChange={(e) => setSearchF(e.target.value)}
               placeholder="Search by name…" className={fieldCls} maxLength={80} />
        <button data-testid="pp-apply-filters" type="submit"
                className="text-[0.7rem] uppercase tracking-[0.22em] py-2 px-4 bg-[var(--ed-ink)] text-white hover:bg-[var(--ed-emerald)] transition-colors rounded-sm flex items-center justify-center gap-2">
          <RefreshCw size={12} /> Apply
        </button>
      </form>

      {error && (
        <div role="alert" data-testid="pp-error" className="mb-4 p-3 border border-red-300 bg-red-50 text-sm text-red-800 rounded-sm flex items-center gap-2">
          <AlertTriangle size={14} /> {error.message}
        </div>
      )}

      <div className="border border-[var(--ed-rule)] rounded-sm bg-white">
        <div className="px-4 py-3 border-b border-[var(--ed-rule)] flex justify-between text-xs text-[var(--ed-ink-muted)]">
          <span data-testid="pp-range">
            {loading ? "Loading…" :
              total === 0 ? "No properties" :
              `Showing ${offset + 1}–${Math.min(offset + PAGE_SIZE, total)} of ${total}`}
          </span>
          <span className="flex gap-2">
            <button data-testid="pp-prev" onClick={() => canPrev && load(offset - PAGE_SIZE)} disabled={!canPrev || loading}
                    className="uppercase tracking-[0.2em] px-3 py-1 border border-[var(--ed-rule)] disabled:opacity-40 hover:border-[var(--ed-ink)] rounded-sm" aria-label="Previous page">Prev</button>
            <button data-testid="pp-next" onClick={() => canNext && load(offset + PAGE_SIZE)} disabled={!canNext || loading}
                    className="uppercase tracking-[0.2em] px-3 py-1 border border-[var(--ed-rule)] disabled:opacity-40 hover:border-[var(--ed-ink)] rounded-sm" aria-label="Next page">Next</button>
          </span>
        </div>

        {!loading && items.length === 0 && (
          <p data-testid="pp-empty" className="p-8 text-center text-sm text-[var(--ed-ink-muted)]">
            No platform properties yet. Use “New Property” to add one.
          </p>
        )}

        {items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="pp-table">
              <thead className="bg-[var(--ed-paper)]">
                <tr className="text-left text-[var(--ed-ink-muted)]">
                  <th className="px-4 py-2.5 ed-overline text-[0.55rem]">Name</th>
                  <th className="px-4 py-2.5 ed-overline text-[0.55rem]">Type</th>
                  <th className="px-4 py-2.5 ed-overline text-[0.55rem]">Neighbourhood</th>
                  <th className="px-4 py-2.5 ed-overline text-[0.55rem]">Status</th>
                  <th className="px-4 py-2.5 ed-overline text-[0.55rem]">Updated</th>
                  <th className="px-4 py-2.5 ed-overline text-[0.55rem]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map(p => (
                  <tr key={p.id} className="border-t border-[var(--ed-rule)] hover:bg-[var(--ed-paper)]/50">
                    <td className="px-4 py-2.5 text-[var(--ed-ink)]" data-testid={`pp-row-name-${p.id}`}>
                      <div>{p.name}</div>
                      <div className="text-[0.7rem] font-mono text-[var(--ed-ink-muted)]">{p.id}</div>
                    </td>
                    <td className="px-4 py-2.5 text-[var(--ed-ink-muted)]">{typeLabel(p.propertyType)}</td>
                    <td className="px-4 py-2.5 text-[var(--ed-ink-muted)]">{p.neighbourhoodName || "—"}</td>
                    <td className="px-4 py-2.5">
                      <StatusPill status={p.status} />
                    </td>
                    <td className="px-4 py-2.5 font-mono text-[0.75rem] text-[var(--ed-ink-muted)]">
                      {p.updatedAt ? p.updatedAt.replace("T"," ").slice(0,19) : "—"}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex gap-2 flex-wrap">
                        <button data-testid={`pp-edit-${p.id}`}
                                onClick={() => setEditing({ mode: "edit", data: p })}
                                className="text-[0.7rem] uppercase tracking-[0.2em] px-3 py-1 border border-[var(--ed-rule)] hover:border-[var(--ed-ink)] rounded-sm">Edit</button>
                        <button data-testid={`pp-history-${p.id}`}
                                onClick={() => setHistoryId(p.id)}
                                className="text-[0.7rem] uppercase tracking-[0.2em] px-3 py-1 border border-[var(--ed-rule)] hover:border-[var(--ed-ink)] rounded-sm flex items-center gap-1">
                          <History size={11} /> History
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <PropertyEditor
          mode={editing.mode}
          data={editing.data}
          brandId={brand?.id}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(offset); }}
        />
      )}
      {historyId && (
        <AuditHistoryDrawer propertyId={historyId} onClose={() => setHistoryId(null)} />
      )}
    </div>
  );
}

// ---------------- Editor modal ----------------

function PropertyEditor({ mode, data, brandId, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const [form, setForm] = useState(() => defaultForm(data));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setError(null);
    try {
      if (!isEdit) {
        await platformCreateProperty({
          brandId,
          name: form.name,
          internalName: form.internalName || null,
          propertyType: form.propertyType,
          timezone: form.timezone,
          neighbourhoodName: form.neighbourhoodName || null,
          capacity: form.capacity ? Number(form.capacity) : null,
          externalReference: form.externalReference || null,
          address: {
            line1: form.address.line1,
            line2: form.address.line2 || null,
            city: form.address.city,
            region: form.address.region || null,
            postalCode: form.address.postalCode,
            countryCode: form.address.countryCode.toUpperCase(),
          },
          operationalContacts: (form.operationalContacts.email || form.operationalContacts.phone) ? {
            email: form.operationalContacts.email || null,
            phone: form.operationalContacts.phone || null,
          } : null,
        });
      } else {
        // Diff-based PATCH.
        const patch = {};
        for (const k of ["name","internalName","propertyType","timezone","neighbourhoodName","externalReference"]) {
          if ((form[k] ?? "") !== (data[k] ?? "")) patch[k] = form[k] || null;
        }
        if (Number(form.capacity || 0) !== Number(data.capacity || 0)) {
          patch.capacity = form.capacity ? Number(form.capacity) : null;
        }
        if (Object.keys(patch).length) {
          await platformPatchProperty(data.id, patch);
        }
      }
      onSaved();
    } catch (e) { setError(readError(e)); }
    finally { setSaving(false); }
  };

  const toggleStatus = async () => {
    const next = data.status === "draft" ? "inactive" : "draft";
    setSaving(true); setError(null);
    try {
      await platformChangePropertyStatus(data.id, next);
      onSaved();
    } catch (e) { setError(readError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div role="dialog" aria-modal="true" data-testid="pp-editor"
         className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form onSubmit={save} className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-[var(--ed-rule)] rounded-sm">
        <div className="px-5 py-4 border-b border-[var(--ed-rule)] flex justify-between items-center">
          <h2 className="font-serif text-xl">{isEdit ? "Edit Property" : "New Property"}</h2>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="pp-editor-close" className="p-1 hover:opacity-70"><XIcon size={16} /></button>
        </div>

        {error && (
          <div role="alert" data-testid="pp-editor-error" className="m-4 p-3 border border-red-300 bg-red-50 text-sm text-red-800 rounded-sm">
            <p>{error.message}</p>
            {error.correlationId && <p className="mt-1 text-xs font-mono">correlation: {error.correlationId}</p>}
          </div>
        )}

        <div className="p-5 space-y-4">
          <Section title="Basic identity">
            <Field label="Name" required>
              <input data-testid="pp-input-name" required maxLength={200} className={fieldCls}
                     value={form.name} onChange={(e) => setForm(f => ({...f, name: e.target.value}))} />
            </Field>
            <Field label="Internal name" hint="Optional. Only visible in the admin.">
              <input data-testid="pp-input-internal-name" maxLength={200} className={fieldCls}
                     value={form.internalName} onChange={(e) => setForm(f => ({...f, internalName: e.target.value}))} />
            </Field>
            <Field label="Property type" required>
              <select data-testid="pp-input-type" required className={fieldCls}
                      value={form.propertyType} onChange={(e) => setForm(f => ({...f, propertyType: e.target.value}))}>
                {TYPES.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
            </Field>
            <Field label="External reference" hint="Optional identifier used in another system (e.g. PMS).">
              <input data-testid="pp-input-external-ref" maxLength={120} className={fieldCls}
                     value={form.externalReference} onChange={(e) => setForm(f => ({...f, externalReference: e.target.value}))} />
            </Field>
          </Section>

          <Section title="Location">
            <Field label="Address line 1" required>
              <input data-testid="pp-input-line1" required maxLength={200} className={fieldCls}
                     value={form.address.line1} onChange={(e) => setForm(f => ({...f, address:{...f.address, line1: e.target.value}}))} />
            </Field>
            <Field label="Address line 2">
              <input maxLength={200} className={fieldCls}
                     value={form.address.line2} onChange={(e) => setForm(f => ({...f, address:{...f.address, line2: e.target.value}}))} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="City" required>
                <input required maxLength={120} className={fieldCls}
                       value={form.address.city} onChange={(e) => setForm(f => ({...f, address:{...f.address, city: e.target.value}}))} />
              </Field>
              <Field label="Region">
                <input maxLength={120} className={fieldCls}
                       value={form.address.region} onChange={(e) => setForm(f => ({...f, address:{...f.address, region: e.target.value}}))} />
              </Field>
              <Field label="Postal code" required>
                <input required maxLength={32} className={fieldCls}
                       value={form.address.postalCode} onChange={(e) => setForm(f => ({...f, address:{...f.address, postalCode: e.target.value}}))} />
              </Field>
              <Field label="Country" required hint="ISO 3166-1 alpha-2">
                <input required maxLength={2} minLength={2} className={fieldCls}
                       value={form.address.countryCode} onChange={(e) => setForm(f => ({...f, address:{...f.address, countryCode: e.target.value}}))} />
              </Field>
            </div>
            <Field label="Neighbourhood">
              <input maxLength={120} className={fieldCls}
                     value={form.neighbourhoodName} onChange={(e) => setForm(f => ({...f, neighbourhoodName: e.target.value}))} />
            </Field>
            <Field label="Timezone" required>
              <input data-testid="pp-input-timezone" required maxLength={64} className={fieldCls}
                     value={form.timezone} onChange={(e) => setForm(f => ({...f, timezone: e.target.value}))}
                     placeholder="Europe/London" />
            </Field>
          </Section>

          <Section title="Operational contact">
            <Field label="Email">
              <input type="email" maxLength={200} className={fieldCls}
                     value={form.operationalContacts.email} onChange={(e) => setForm(f => ({...f, operationalContacts:{...f.operationalContacts, email: e.target.value}}))} />
            </Field>
            <Field label="Phone">
              <input maxLength={32} className={fieldCls}
                     value={form.operationalContacts.phone} onChange={(e) => setForm(f => ({...f, operationalContacts:{...f.operationalContacts, phone: e.target.value}}))} />
            </Field>
            <Field label="Capacity" hint="Placeholder; not used by any customer-facing feature yet.">
              <input type="number" min={1} max={100} className={fieldCls}
                     value={form.capacity || ""} onChange={(e) => setForm(f => ({...f, capacity: e.target.value}))} />
            </Field>
          </Section>

          <Section title="Lifecycle">
            <p className="text-xs text-[var(--ed-ink-muted)]">
              Current status: <StatusPill status={isEdit ? data.status : "draft"} />
            </p>
            <p className="text-xs text-[var(--ed-ink-muted)]" data-testid="pp-status-help">{STATUS_HELP}</p>
            {isEdit && (
              <button type="button" onClick={toggleStatus} disabled={saving}
                      data-testid="pp-toggle-status"
                      className="text-[0.7rem] uppercase tracking-[0.22em] px-4 py-2 border border-[var(--ed-rule)] hover:border-[var(--ed-ink)] rounded-sm disabled:opacity-40">
                {saving ? "Saving…" : data.status === "draft" ? "Deactivate" : "Reactivate to Draft"}
              </button>
            )}
          </Section>
        </div>

        <div className="px-5 py-4 border-t border-[var(--ed-rule)] flex justify-end gap-3">
          <button type="button" onClick={onClose} className="text-[0.7rem] uppercase tracking-[0.22em] px-4 py-2 border border-[var(--ed-rule)] rounded-sm">Cancel</button>
          <button type="submit" disabled={saving} data-testid="pp-editor-save"
                  className="text-[0.7rem] uppercase tracking-[0.22em] px-5 py-2 bg-[var(--ed-ink)] text-white hover:bg-[var(--ed-emerald)] rounded-sm disabled:opacity-40">
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create Draft"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------------- Audit history drawer ----------------

function AuditHistoryDrawer({ propertyId, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const d = await platformListPropertyAudit(propertyId, { limit: 50 });
        setItems(d.items);
      } catch (e) { setError(readError(e)); }
      finally { setLoading(false); }
    })();
  }, [propertyId]);

  return (
    <div role="dialog" aria-modal="true" data-testid="pp-history"
         className="fixed inset-0 z-50 flex items-start justify-end bg-black/40">
      <div className="bg-white w-full max-w-lg h-full overflow-y-auto border-l border-[var(--ed-rule)]">
        <div className="px-5 py-4 border-b border-[var(--ed-rule)] flex justify-between items-center">
          <h2 className="font-serif text-xl">Property audit history</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 hover:opacity-70"><XIcon size={16} /></button>
        </div>
        {error && (
          <div role="alert" className="m-4 p-3 border border-red-300 bg-red-50 text-sm text-red-800 rounded-sm">{error.message}</div>
        )}
        {loading && <p className="p-6 text-sm text-[var(--ed-ink-muted)]">Loading…</p>}
        {!loading && items.length === 0 && (
          <p className="p-6 text-sm text-[var(--ed-ink-muted)]">No history for this property.</p>
        )}
        <ul className="divide-y divide-[var(--ed-rule)]">
          {items.map(e => (
            <li key={e.id} className="p-4">
              <p className="text-[0.75rem] font-mono text-[var(--ed-ink-muted)]">{e.occurred_at.replace("T"," ").slice(0,19)}</p>
              <p className="font-mono text-sm">{e.action} <span className="text-[0.65rem] uppercase tracking-[0.2em] ml-2 text-[var(--ed-ink-muted)]">{e.outcome}</span></p>
              <p className="text-xs text-[var(--ed-ink-muted)]">correlation {e.correlation_id}</p>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ---------------- Small helpers ----------------

const fieldCls = "w-full text-sm px-3 py-2 border border-[var(--ed-rule)] bg-white focus:outline-none focus:border-[var(--ed-ink)] rounded-sm";

function Section({ title, children }) {
  return (
    <fieldset className="border border-[var(--ed-rule)] rounded-sm p-4">
      <legend className="px-2 ed-overline text-[0.55rem]">{title}</legend>
      <div className="space-y-3">{children}</div>
    </fieldset>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <div>
      <label className="ed-overline text-[0.55rem] mb-1.5 block">
        {label}{required && <span aria-hidden className="text-red-600 ml-0.5">*</span>}
      </label>
      {children}
      {hint && <p className="text-[0.7rem] text-[var(--ed-ink-muted)] mt-1">{hint}</p>}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    draft:    "bg-neutral-100 text-neutral-800 border-neutral-300",
    inactive: "bg-amber-50 text-amber-800 border-amber-300",
  };
  const cls = map[status] || "bg-neutral-100 text-neutral-700 border-neutral-300";
  return (
    <span className={`inline-block text-[0.6rem] uppercase tracking-[0.2em] px-2 py-0.5 border rounded-full ${cls}`}
          data-testid={`pp-status-${status}`}>
      {status}
    </span>
  );
}

function typeLabel(v) {
  return (TYPES.find(t => t.v === v) || {}).l || v;
}

function defaultForm(data) {
  return {
    name: data?.name || "",
    internalName: data?.internalName || "",
    propertyType: data?.propertyType || "serviced_apartment",
    timezone: data?.timezone || "Europe/London",
    neighbourhoodName: data?.neighbourhoodName || "",
    capacity: data?.capacity || "",
    externalReference: data?.externalReference || "",
    address: {
      line1:       data?.address?.line1 || "",
      line2:       data?.address?.line2 || "",
      city:        data?.address?.city || "",
      region:      data?.address?.region || "",
      postalCode:  data?.address?.postalCode || "",
      countryCode: data?.address?.countryCode || "GB",
    },
    operationalContacts: {
      email: data?.operationalContacts?.email || "",
      phone: data?.operationalContacts?.phone || "",
    },
  };
}

function readError(e) {
  const body = e?.response?.data;
  return {
    message: body?.message || "The Platform API could not process the request.",
    correlationId: body?.correlation_id || e?.response?.headers?.["x-correlation-id"],
  };
}
